import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { graphql } from "react-relay";
import { useFragment } from "react-relay";
import { useLocation } from "react-router";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import useEffectAfterMount from "~/lib/hooks/useEffectAfterMount";
import { commitMutationPromise } from "~/lib/relayUtil";
import { useNewest } from "~/lib/utils";
import { environment } from "~/models";
import { Checkbox, type CheckboxProps, Tooltip } from "~/ui";
import { TextArea } from "~/ui/legacyFellowIndex";

import { ViewportContext } from "../common";
import { Divider, Label, Row, SubLabel } from "./SettingsModal/shared";

import type {
	EventFilters_viewer$data as EventFiltersQueryResponse,
	EventFilters_viewer$key as EventFiltersViewerKey,
} from "~/__generated__/EventFilters_viewer.graphql";
import type { EventFiltersUpdateMutation } from "~/__generated__/EventFiltersUpdateMutation.graphql";

import styles from "./css/settings.module.css";

const eventFiltersFragment = graphql`
	fragment EventFilters_viewer on Viewer {
		user {
			internalAttributes {
				dateConfigured
			}
			eventFilters {
				includeCancelled
				includeHidden
				includeDeclined
				includeAllDay
				includeOutOfOffice
				includeFocusTime
				includePersonalMeetings
				filterKeywords
			}
		}
		calendars {
			edges {
				node {
					source
				}
			}
		}
	}
`;

const updateEventFilters = graphql`
	mutation EventFiltersUpdateMutation($filters: UserEventFiltersInput!) {
		updateEventFilters(filters: $filters) {
			eventFilters {
				includeCancelled
				includeHidden
				includeDeclined
				includeAllDay
				includeOutOfOffice
				includeFocusTime
				includePersonalMeetings
				filterKeywords
			}
		}
	}
`;

type eventFiltersResponse = NonNullable<EventFiltersQueryResponse["user"]["eventFilters"]>;
type filterName = keyof eventFiltersResponse;
export type nonKeywords = Exclude<filterName, "filterKeywords">;

export type EventFilterType = {
	name: nonKeywords;
	displayText: string;
	tooltip?: string;
};

type NonKeywordStates = {
	[key in nonKeywords]?: boolean;
};

export type FiltersState = NonKeywordStates & {
	filterKeywords?: string[];
};

type CalendarProviderType = "google" | "office365" | "both" | "none";

const getBooleanFiltersFromResponse = (
	eventFilters: EventFiltersQueryResponse["user"]["eventFilters"],
	calendarProvider: CalendarProviderType,
) => {
	const { filterKeywords: _, ..._booleanFilters } = eventFilters ?? {};

	return Object.entries(_booleanFilters).reduce((acc, [name, value]) => {
		if (calendarProvider === "office365" && name === "includeDeclined") {
			return acc;
		}

		acc[name as nonKeywords] = Boolean(value);
		return acc;
	}, {} as NonKeywordStates);
};

const getFilterKeywordsFromResponse = (eventFilters: EventFiltersQueryResponse["user"]["eventFilters"]) => {
	if (eventFilters?.filterKeywords && Array.isArray(eventFilters?.filterKeywords))
		return eventFilters.filterKeywords as string[];

	return [];
};

export function useEventFilters(viewerKey: EventFiltersViewerKey) {
	const { user, calendars } = useFragment(eventFiltersFragment, viewerKey);

	const calendarProvider = useMemo<CalendarProviderType>(() => {
		const providers = calendars?.edges.filter(c => c?.node?.source !== "internal").map(c => c?.node?.source);
		let provider: CalendarProviderType = "none";
		if (providers?.includes("google") && providers?.includes("office365")) {
			provider = "both";
		} else if (providers?.includes("google")) {
			provider = "google";
		} else if (providers?.includes("office365")) {
			provider = "office365";
		} else {
			provider = "none";
		}

		return provider;
	}, [calendars]);

	// a value of true it means the filter is _not_ active (the meeting type is shown)
	const [booleanFilters, setBooleanFilters] = useState<NonKeywordStates>(() =>
		getBooleanFiltersFromResponse(user.eventFilters, calendarProvider),
	);
	const [filterKeywords, setFilterKeywords] = useState<string[]>(() =>
		getFilterKeywordsFromResponse(user.eventFilters),
	);

	React.useEffect(() => {
		setBooleanFilters(getBooleanFiltersFromResponse(user.eventFilters, calendarProvider));
		setFilterKeywords(getFilterKeywordsFromResponse(user.eventFilters));
	}, [calendarProvider, user]);

	const eventFilterOptions: EventFilterType[] = useMemo(() => {
		const filters: EventFilterType[] = [
			{
				name: "includeCancelled",
				displayText: "cancelled meetings",
				tooltip: calendarProvider === "office365" ? "This includes both cancelled and declined meetings" : undefined,
			},
			{
				name: "includeHidden",
				displayText: "hidden meetings",
				tooltip: "These are meetings that were manually hidden via the triple dot menu in Fellow.",
			},
			...(calendarProvider !== "office365"
				? [{ name: "includeDeclined" as nonKeywords, displayText: "declined meetings" }]
				: []),
			{ name: "includeAllDay", displayText: "all-day events" },
			{
				name: "includePersonalMeetings",
				displayText: "personal events",
				tooltip: `These are events where you are the only attendee.`,
			},
		];
		if (calendarProvider === "google") {
			filters.push(
				{
					name: "includeOutOfOffice",
					displayText: "“Out of office” events",
					tooltip: `The “Out of office” event type is automatically detected from your calendar provider.`,
				},
				{
					name: "includeFocusTime",
					displayText: "“Focus time” events",
					tooltip: `The “Focus time” event type is automatically detected from your calendar provider.`,
				},
			);
		} else if (calendarProvider === "office365") {
			filters.push({
				name: "includeOutOfOffice",
				displayText: "“Away” events",
				tooltip: `The “Away” event type is automatically detected from your calendar provider.`,
			});
		} else if (calendarProvider === "both") {
			filters.push({
				name: "includeOutOfOffice",
				displayText: "“Out of office” or “Away” events",
				tooltip: `The “Out of office” or “Away” event type is automatically detected from your calendar provider.`,
			});
		}

		return filters;
	}, [calendarProvider]);

	const updateFilters = useCallback(async (updatedFilterState: FiltersState) => {
		const response = await commitMutationPromise<EventFiltersUpdateMutation>(environment, {
			mutation: updateEventFilters,
			variables: {
				filters: {
					...updatedFilterState,
					// Trim the keywords and remove empty strings
					filterKeywords: updatedFilterState.filterKeywords
						?.map(keyword => keyword.trim())
						.filter(keyword => keyword !== ""),
				},
			},
		});

		if (!response.updateEventFilters || !response.updateEventFilters.eventFilters) {
			return;
		}
		const { eventFilters } = response.updateEventFilters;

		const { filterKeywords: responseFilterKeywords, ...responseFilters } = eventFilters;

		const filterKeywords: string[] = [];
		if (responseFilterKeywords !== null) {
			responseFilterKeywords.forEach(word => {
				if (word !== null) filterKeywords.push(word);
			});
		}

		const filters: NonKeywordStates = {};
		Object.entries(responseFilters).forEach(([name, value]) => (filters[name as nonKeywords] = Boolean(value)));

		setBooleanFilters(filters);
		setFilterKeywords(filterKeywords);
	}, []);

	return React.useMemo(
		() => ({
			eventFilterOptions,
			updateFilters,
			filters: {
				...booleanFilters,
				filterKeywords,
			} as FiltersState,
			user,
		}),
		[eventFilterOptions, updateFilters, booleanFilters, filterKeywords, user],
	);
}

interface EventFiltersSettingsProps {
	viewer: EventFiltersViewerKey;
}

export default function EventFiltersSettings({ viewer: viewerKey }: EventFiltersSettingsProps) {
	const location = useLocation();
	const { isMobile } = useContext(ViewportContext);
	useEffect(() => {
		if (!isMobile && location.hash === "#event_filters") {
			// The element ID needs to be different so default browser scroll doesn't kick in, and instead we do a smooth one
			const element = document.getElementById("event_filters_settings");
			if (element) {
				setTimeout(() => element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" }), 200);
			}
		}
	}, [location, isMobile]);

	const { eventFilterOptions, updateFilters, filters: _filters } = useEventFilters(viewerKey);
	const [uiState, setUIState] = React.useState<FiltersState>(() => {
		return {
			..._filters,
			filterKeywords: _filters.filterKeywords ?? [],
		};
	});
	const [duplicateKeywordsError, setDuplicateKeywordsError] = useState<boolean>(false);
	const filters = useNewest(_filters, uiState);

	const onCheckboxChange = React.useCallback<(filterName: nonKeywords) => CheckboxProps["onCheckedChange"]>(
		filterName => checked => {
			setUIState({ ...filters, [filterName]: checked });
		},
		[filters],
	);

	const onKeywordsChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setUIState({
				...uiState,
				// Since we join the words with ", " (comma space), we need to undo that here.
				// Ultimately we want to split on "," (comma), so we split for that too.
				filterKeywords: e.target.value.split(", ").flatMap(word => word.split(",")),
			});
		},
		[uiState],
	);

	const keywordsForDisplay = useMemo(() => {
		// In a perfect world we would use the value from `useNewest`, however that makes typing not so smooth.
		// Instead, we continue to use uiState since, as of writing, there's no other way to update the keywords. This means
		// 	that when an update comes from relay, the value should be the direct result of what is currently in the input.
		if (!uiState.filterKeywords) return "";
		return uiState.filterKeywords.join(", ");
	}, [uiState]);

	const debouncedFilters = useDebouncedValue(uiState, 500);
	useEffectAfterMount(() => {
		// determine if any keywords are duplicates
		const removedNewLines = debouncedFilters.filterKeywords?.reduce((keywords, word) => {
			const splitWord = word.split(/[\n,]+/).filter(word => word !== "");
			return [...keywords, ...splitWord];
		}, new Array<string>());
		const trimmedKeywords = removedNewLines?.map(keyword => keyword.toLocaleLowerCase().trim());
		const uniqueKeywords = new Set(trimmedKeywords);
		setDuplicateKeywordsError(uniqueKeywords.size !== removedNewLines?.length);
		updateFilters({ ...debouncedFilters, filterKeywords: Array.from(uniqueKeywords) });
	}, [debouncedFilters, updateFilters]);

	return (
		<div id="event_filters_settings" className={styles.eventFiltersWrapper}>
			<Row>
				<Row.Left>
					<Label>Meeting visibility</Label>
					<SubLabel>Choose which events are visible to you in your Fellow calendar.</SubLabel>
				</Row.Left>
			</Row>
			{eventFilterOptions.map(option => (
				<div key={option.name} className={styles.eventFilter}>
					<label className={styles.labelContent}>
						<Checkbox checked={Boolean(filters[option.name])} onCheckedChange={onCheckboxChange(option.name)} />
						&nbsp; Show {option.displayText}
					</label>
					{option.tooltip && (
						<Tooltip content={option.tooltip} placement="top" className={styles.eventFilterTooltip} maxWidth="30ch">
							<FontAwesomeIcon icon={faInfoCircle} size="sm" className={styles.eventFilterInfoCircle} />
						</Tooltip>
					)}
				</div>
			))}
			<Divider />
			<Row>
				<Row.Left>
					<Label>Exclude meetings by keyword</Label>
					<SubLabel>
						Meeting titles with words that start with these keywords will be hidden from your Fellow calendar.
					</SubLabel>
				</Row.Left>
			</Row>
			<TextArea
				resizable
				rows={3}
				value={keywordsForDisplay}
				onChange={onKeywordsChange}
				className={cx(styles.eventFilterKeywords, {
					[styles.keywordError]: duplicateKeywordsError,
				})}
				placeholder="Lunch, OOO, Focus, Workout, etc."
			/>
			{duplicateKeywordsError && (
				<div className={cx(styles.keywordMessage, styles.keywordErrorText)}>Duplicate keywords will be removed.</div>
			)}
			<SubLabel>Use commas to separate each keyword. Keywords are not case sensitive.</SubLabel>
		</div>
	);
}

export type { EventFiltersViewerKey };
